<template>
  <v-row dense>
    <v-col cols="12" md="7">
      <v-card class="qa-card-sm" variant="outlined">
        <v-card-title class="py-3"><span class="text-subtitle-1">{{ $t('qaPolTitle') }}</span></v-card-title>
        <v-divider />
        <div class="pa-4">
          <div class="qa-set-row">
            <div>
              <div class="text-body-2 font-weight-medium">Pass mark</div>
              <div class="text-caption text-medium-emphasis">{{ $t('qaPolPassMarkDesc') }}</div>
            </div>
            <div class="d-flex align-center ga-1">
              <v-btn icon size="x-small" variant="tonal" @click="bump('passMark', -1)"><v-icon size="16">mdi-minus</v-icon></v-btn>
              <span class="qa-num qa-stepper-value">{{ policy.passMark }}</span>
              <v-btn icon size="x-small" variant="tonal" @click="bump('passMark', 1)"><v-icon size="16">mdi-plus</v-icon></v-btn>
            </div>
          </div>

          <div class="qa-set-row">
            <div>
              <div class="text-body-2 font-weight-medium">Borderline band</div>
              <div class="text-caption text-medium-emphasis">
                {{ $t('qaPolBorderlineDesc', { range: `${policy.passMark}–${policy.passMark + policy.borderlineBand - 1}` }) }}
              </div>
            </div>
            <div class="d-flex align-center ga-1">
              <v-btn icon size="x-small" variant="tonal" @click="bump('borderlineBand', -1)"><v-icon size="16">mdi-minus</v-icon></v-btn>
              <span class="qa-num qa-stepper-value">{{ policy.borderlineBand }}</span>
              <v-btn icon size="x-small" variant="tonal" @click="bump('borderlineBand', 1)"><v-icon size="16">mdi-plus</v-icon></v-btn>
            </div>
          </div>

          <!-- แทนแถว "ข้อยกเว้นตาม channel / ทีม" เดิมที่อ่านอย่างเดียวและอวดเกินจริง —
               backend รองรับแค่ pass mark แยก chat/voice (QaChannelPassMark) ไม่มี override รายทีม -->
          <div class="qa-set-row">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaPolChannelPassMark') }}</div>
              <div class="text-caption text-medium-emphasis">{{ $t('qaPolChannelPassMarkDesc') }}</div>
            </div>
            <div class="d-flex align-center ga-2">
              <v-text-field
                :model-value="policy.channelPassMark.chat"
                label="chat"
                type="number"
                min="50"
                max="95"
                density="compact"
                variant="outlined"
                hide-details
                clearable
                style="max-width: 110px"
                @update:model-value="setChannelMark('chat', $event)"
                @click:clear="policy.channelPassMark.chat = null"
              />
              <v-text-field
                :model-value="policy.channelPassMark.voice"
                label="voice"
                type="number"
                min="50"
                max="95"
                density="compact"
                variant="outlined"
                hide-details
                clearable
                style="max-width: 110px"
                @update:model-value="setChannelMark('voice', $event)"
                @click:clear="policy.channelPassMark.voice = null"
              />
            </div>
          </div>

          <div class="qa-set-row">
            <div>
              <div class="text-body-2 font-weight-medium">Gate hard-fail</div>
              <div class="text-caption text-medium-emphasis">
                {{ $t('qaPolGateHardFailDesc') }}
              </div>
            </div>
            <v-switch v-model="policy.gateHardFail" color="primary" density="compact" hide-details inset />
          </div>

          <div class="qa-set-row">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaPolRounding') }}</div>
              <div class="text-caption text-medium-emphasis">{{ $t('qaPolRoundingDesc') }}</div>
            </div>
            <v-select v-model="policy.rounding" :items="roundingItems" density="compact" variant="outlined" hide-details style="max-width: 130px" />
          </div>

          <!-- ตัดแถว "วันที่เริ่มมีผล" เดิมทิ้ง — policy ไม่มีวันเริ่ม มันมีผลทันทีและย้อนหลัง
               เพราะผลตัดสินคำนวณตอนอ่านจาก policy ปัจจุบันเสมอ (QaVerdict.Resolve)
               การแสดงวันที่ = สัญญาว่าเคสเก่าไม่ถูกกระทบ ซึ่งไม่จริง -->
          <v-alert type="info" variant="tonal" density="compact" class="mt-3">
            <span class="text-body-2">
              {{ $t('qaPolRetroNote') }}
            </span>
          </v-alert>

          <div class="d-flex align-center flex-wrap ga-3 mt-4">
            <v-btn color="primary" variant="flat" :loading="saving" prepend-icon="mdi-content-save-outline" @click="save">{{ $t('qaPolSave') }}</v-btn>
            <span v-if="updatedAt" class="text-caption text-medium-emphasis">{{ $t('qaSetLastEditedBy') }} {{ updatedBy || $t('qaSetUnknown') }} · {{ updatedAtText }}</span>
            <span v-else class="text-caption text-medium-emphasis">{{ $t('qaSetNeverSaved') }}</span>
          </div>
        </div>
      </v-card>
    </v-col>

    <v-col cols="12" md="5">
      <v-card class="qa-card-sm" variant="outlined">
        <v-card-title class="d-flex align-center py-3">
          <span class="text-subtitle-1">{{ $t('qaPolImpact') }}</span>
          <v-spacer />
          <span class="text-caption text-medium-emphasis">{{ $t('qaPolImpactSource') }}</span>
        </v-card-title>
        <v-divider />
        <div class="pa-4">
          <div v-for="band in bands" :key="band.key" class="mb-3">
            <div class="d-flex justify-space-between text-body-2 mb-1">
              <span :class="`text-${band.color}`">{{ band.label }}</span>
              <span class="qa-num">{{ band.value }} · {{ impact.total ? Math.round((band.value / impact.total) * 100) : 0 }}%</span>
            </div>
            <v-progress-linear :model-value="impact.total ? (band.value / impact.total) * 100 : 0" :color="band.color" height="8" rounded bg-color="grey-lighten-3" />
          </div>

          <v-alert :type="impact.changedCount ? 'warning' : 'info'" variant="tonal" density="compact" class="mt-4">
            <span class="text-body-2">
              {{ $t('qaPolChangedNote', { changed: impact.changedCount, total: impact.total }) }}
            </span>
          </v-alert>
        </div>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
/**
 * Pass / Fail Policy + Impact preview (Q9: ของจริงแล้ว)
 *
 * จุดสำคัญ: ทุกครั้งที่ขยับตัวเลข จะยิง settings/policy/impact ให้ server คำนวณกับ
 * เคสที่ประเมินแล้ว**จริง**ให้ดูทันที (debounce 220ms) — กันการปรับเกณฑ์แบบไม่ตั้งใจ
 * ซึ่งกระทบการประเมินผลงานพนักงาน**ย้อนหลังทั้งหมด** เพราะผลตัดสินคำนวณตอนอ่าน
 * จาก policy ปัจจุบันเสมอ ไม่มีวันเริ่มมีผล
 */
import qaConsole from '@/services/qaConsole';

export default {
  name: 'QaPolicySettings',
  emits: ['saved'],
  data() {
    return {
      // ค่าตั้งต้นตรงกับ QaPolicySettings.Default ฝั่ง server — โหลดไม่ทัน/ล้ม หน้าจอก็ไม่โกหก
      policy: { passMark: 80, borderlineBand: 5, gateHardFail: true, rounding: 0.5, channelPassMark: { chat: null, voice: null } },
      impact: { total: 0, pass: 0, borderline: 0, fail: 0, changedCount: 0 },
      updatedAt: null,
      updatedBy: null,
      saving: false,
      previewTimer: null,
    };
  },
  computed: {
    // ⚠️ computed ไม่ใช่ data — this.$t ใน data() ไม่ reactive ต่อ locale
    roundingItems() {
      return [
        { title: '0.5', value: 0.5 },
        { title: '1.0', value: 1 },
        { title: this.$t('qaPolRoundingNone'), value: 0 },
      ];
    },
    bands() {
      return [
        { key: 'pass', label: this.$t('qaPolBandPass'), value: this.impact.pass, color: 'success' },
        { key: 'borderline', label: this.$t('qaPolBandBorderline'), value: this.impact.borderline, color: 'warning' },
        { key: 'fail', label: this.$t('qaPolBandFail'), value: this.impact.fail, color: 'error' },
      ];
    },
    updatedAtText() {
      if (!this.updatedAt) return '';
      try {
        return new Date(this.updatedAt).toLocaleString('th-TH');
      } catch {
        return this.updatedAt;
      }
    },
  },
  watch: {
    policy: {
      deep: true,
      handler() {
        clearTimeout(this.previewTimer);
        this.previewTimer = setTimeout(() => this.loadImpact(), 220);
      },
    },
  },
  async mounted() {
    try {
      const res = await qaConsole.getPolicy();
      this.applyResult(res);
    } catch (err) {
      this.$emit('saved', { ok: false, message: this.$t('qaPolLoadFailed', { msg: err.message }) });
    }
    this.loadImpact();
  },
  beforeUnmount() {
    clearTimeout(this.previewTimer);
  },
  methods: {
    applyResult(res) {
      if (res && res.value) {
        this.policy = { ...this.policy, ...res.value };
        // server อาจส่ง channelPassMark เป็น null — หน้าจอ bind ลึกถึง .chat/.voice ต้องมี object เสมอ
        this.policy.channelPassMark = { chat: null, voice: null, ...(res.value.channelPassMark || {}) };
      }
      this.updatedAt = res ? res.updatedAt : null;
      this.updatedBy = res ? res.updatedBy : null;
    },
    bump(key, delta) {
      // ขอบเดียวกับ Normalized() ฝั่ง server — หน้าจอไม่เสนอค่าที่ server จะบีบทิ้งอยู่ดี
      const limits = { passMark: [50, 95], borderlineBand: [0, 15] };
      const [min, max] = limits[key];
      this.policy[key] = Math.max(min, Math.min(max, this.policy[key] + delta));
    },
    setChannelMark(channel, value) {
      // ช่องว่าง/ลบทิ้ง = null = "ใช้ค่ารวม" — ห้ามส่ง '' หรือ NaN ไปให้ server เดา
      const n = parseInt(value, 10);
      this.policy.channelPassMark[channel] = Number.isNaN(n) ? null : n;
    },
    async loadImpact() {
      try {
        this.impact = await qaConsole.getPolicyImpact(this.policy);
      } catch {
        // preview ล้มเหลวไม่ควรทำให้แก้ค่าต่อไม่ได้
      }
    },
    async save() {
      this.saving = true;
      try {
        // รับค่าที่ server คืน (clamp แล้ว) กลับมาแสดง — ไม่ใช่ค่าที่พิมพ์
        const res = await qaConsole.savePolicy(this.policy);
        this.applyResult(res);
        this.$emit('saved', {
          ok: true,
          message: this.$t('qaPolSaved'),
        });
        this.loadImpact();
      } catch (err) {
        this.$emit('saved', { ok: false, message: this.$t('qaPolSaveFailed', { msg: err.message }) });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.qa-set-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 13px 0;
  border-bottom: 1px solid #f1f3f5;
}
.qa-set-row:last-of-type {
  border-bottom: none;
}
.qa-set-row > div:first-child {
  flex: 1;
  min-width: 0;
}
.qa-stepper-value {
  min-width: 42px;
  text-align: center;
  font-weight: 600;
}
</style>
